import * as React from "react";
import { SauerPreviewViewer, parseSauerData } from "./SauerPreviewViewer";
import { SoloplanPreviewViewer, parseSoloplanData } from "./SoloplanPreviewViewer";
import { KomalogPreviewViewer, parseKomalogData } from "./KomalogPreviewViewer";
import { TranslogicaPreviewViewer, parseTranslogicaData } from "./TranslogicaPreviewViewer";
import { WinSpedPreviewViewer, parseWinSpedData } from "./WinSpedPreviewViewer";
import { FileSelector } from "../converter/ExportSim/output/FileSelector";

export interface PreviewViewerProps {
    output: string;
    fullConverterOutput?: string;
    selectedFileIndex?: number;
    onFileSelect?: (index: number) => void;
}

type DetectedFormat = "sauer" | "soloplan" | "komalog" | "translogica" | "winsped" | "unknown";

function detectFormat(output: string): DetectedFormat {
    if (!output) return "unknown";

    // WinSped LIS-Format
    if (parseWinSpedData(output)) return "winsped";

    // XML-basierte Formate
    if (output.trim().startsWith("<")) {
        if (parseTranslogicaData(output)) return "translogica";
        if (parseKomalogData(output))     return "komalog";
    }

    try {
        const parsed = JSON.parse(output);

        if (Array.isArray(parsed.order) && parsed.header?.exportItemReference !== undefined) {
            return "soloplan";
        }

        if (parseSauerData(output)) return "sauer";

        if (Array.isArray(parsed.resultFiles)) {
            for (const file of parsed.resultFiles) {
                if (!file?.base64Content) continue;
                try {
                    const decoded = decodeURIComponent(escape(atob(file.base64Content)));
                    const fmt = detectFormat(decoded);
                    if (fmt !== "unknown") return fmt;
                } catch { /* weiter */ }
            }
        }
    } catch { /* kein JSON */ }
    return "unknown";
}

const noDataStyle: React.CSSProperties = {
    color: "#888",
    fontStyle: "italic",
    padding: 16,
    fontFamily: "Segoe UI, Tahoma, sans-serif",
    fontSize: 12,
};

export function PreviewViewer({ output, fullConverterOutput, selectedFileIndex, onFileSelect }: PreviewViewerProps) {
    const format = React.useMemo(() => detectFormat(output), [output]);

    const fileSelector = fullConverterOutput && onFileSelect != null ? (
        <FileSelector
            converterOutput={fullConverterOutput}
            selectedIndex={selectedFileIndex ?? 0}
            onSelect={onFileSelect}
        />
    ) : null;

    const content = (() => {
        if (format === "sauer")   return <SauerPreviewViewer output={output} />;
        if (format === "soloplan") return <SoloplanPreviewViewer output={output} />;
        if (format === "komalog")     return <KomalogPreviewViewer output={output} />;
        if (format === "translogica") return <TranslogicaPreviewViewer output={output} />;
        if (format === "winsped")     return <WinSpedPreviewViewer output={output} />;
        return (
            <div style={noDataStyle}>
                Kein unterstütztes Format erkannt. Unterstützt werden: Sauer und Soloplan.
            </div>
        );
    })();

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {fileSelector}
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {content}
            </div>
        </div>
    );
}
